import { mt } from '@passionware/monads';
import { promiseState } from '@passionware/platform-react';
import { RefreshCw } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentTeamName } from 'v4/entities/common/hooks/useTeam';
import { Button } from 'v5/design-system/Button';
import { cn } from 'v5/platform/dom/cn';
import { ManualSyncResponse } from '../../../../api/visitor-vendor-state/visitor-vendor-state.api';
import { myServices } from '../../../../services/services.connected.app';

interface ManualSyncButtonProps {
  bookingId: number;
  onSyncComplete?: (result: ManualSyncResponse) => void;
  variant?: 'icon' | 'button';
  needsSync?: boolean;
  className?: string;
}

/**
 * Manual sync button component for booking tab.
 * Provides prominent sync functionality with loading states and toast notifications.
 */
export const ManualSyncButton: React.FC<ManualSyncButtonProps> = ({
  bookingId,
  onSyncComplete,
  variant = 'button',
  needsSync = false,
  className
}) => {
  const { t } = useTranslation('visitor-management');
  const teamName = useCurrentTeamName();

  const syncMutation = promiseState.useMutation(
    async (params: { teamName: string; bookingId: number }) => {
      const result = await myServices.visitorVendorStateService.manualSync(
        params.teamName,
        params.bookingId
      );
      return result;
    }
  );

  const handleSync = async (e: React.MouseEvent) => {
    e.stopPropagation();

    try {
      const result = await syncMutation.track({
        teamName: teamName || '',
        bookingId
      });

      // Websockets handle success cases and errors (preparation_errors, api_errors).
      // We only need to show feedback for not_eligible cases, which are skipped and don't trigger websockets.
      const { summary } = result;

      // Show info toast only when vendors are not eligible (skipped by websockets)
      if (summary.not_eligible > 0) {
        myServices.toastService.open({
          type: 'info',
          message:
            summary.total === 1
              ? 'Vendor is not eligible for sync'
              : summary.not_eligible === summary.total
                ? `All ${summary.total} vendors are not eligible for sync`
                : `${summary.not_eligible} of ${summary.total} vendor(s) not eligible for sync`
        });
      }

      onSyncComplete?.(result);
    } catch (error) {
      console.error('Manual sync error:', error);
      myServices.toastService.open({
        type: 'error',
        message: 'Failed to trigger sync'
      });
    }
  };

  const isLoading = mt.isInProgress(syncMutation.state);

  if (variant === 'icon') {
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={handleSync}
        onClickEvenWhenDisabled={handleSync}
        disabled={isLoading}
        visuallyDisabled={!needsSync}
        className={cn('h-8 w-8 p-0', className)}
      >
        <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
      </Button>
    );
  }

  return (
    <Button
      type="button"
      variant={needsSync ? 'primaryAlt' : 'secondary'}
      size="lg"
      onClick={handleSync}
      disabled={isLoading}
      className={cn('gap-3 font-medium', className)}
    >
      <RefreshCw className={cn('h-5 w-5', isLoading && 'animate-spin')} />

      <span>Sync with Vendors</span>
    </Button>
  );
};
